import React, { useRef, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import cx from 'classnames';
import { FiZap, FiRefreshCw } from 'react-icons/fi';

import Loader from '~components/loader';
import Radio from '~components/forms/radio';

import { requestSuggestedNote } from './service';

import './style.scss';

const MAX_SUGGESTIONS = 2;

// Renders a small action that asks the backend for ad-text suggestions and
// hands the chosen one back through onApply. buildPayload owns all the
// form-specific data mapping and returns null when there isn't enough to make a
// useful request, which keeps this component agnostic to whether it sits in the
// customer or worker form.
//
// The first suggestion is applied immediately so a single click still fills the
// field. Asking for a second one turns the control into a picker: both versions
// are shown and the user selects which one lands in the field, then can still
// edit it before submitting.
const NoteSuggest = ({ buildPayload, onApply, disabled = false, className }) => {
  const intl = useIntl();
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorKey, setErrorKey] = useState(null);

  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const payload = buildPayload();
  const hasData = payload !== null;
  const count = suggestions.length;
  const reachedMax = count >= MAX_SUGGESTIONS;
  const isDisabled = disabled || !hasData || reachedMax;

  const handleSelect = (value) => {
    const index = Number(value);
    if (suggestions[index] === undefined) {
      return;
    }
    setSelectedIndex(index);
    onApply(suggestions[index]);
  };

  const handleClick = async () => {
    if (inFlightRef.current || isDisabled) {
      return;
    }

    const currentPayload = buildPayload();
    if (!currentPayload) {
      return;
    }

    inFlightRef.current = true;
    setIsLoading(true);
    setErrorKey(null);

    try {
      const note = await requestSuggestedNote(currentPayload);
      if (!mountedRef.current) {
        return;
      }
      setSuggestions((current) => [...current, note]);
      // Apply the very first suggestion straight away so one click still fills
      // the field. Once a second version exists we leave the choice to the user
      // and don't move their current selection.
      if (count === 0) {
        setSelectedIndex(0);
        onApply(note);
      }
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      const isNotFound = error?.response?.status === 404;
      setErrorKey(isNotFound ? 'error-unavailable' : 'error');
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
      inFlightRef.current = false;
    }
  };

  const isFirst = count === 0;
  const Icon = isFirst ? FiZap : FiRefreshCw;

  let labelId = 'suggest';
  if (!isFirst) {
    labelId = reachedMax ? 'regenerate-max' : 'regenerate';
  }

  const label = intl.formatMessage({
    id: `components.note-suggest.${labelId}`,
    defaultMessage: {
      suggest: 'Suggest text',
      regenerate: 'Try another version',
      'regenerate-max': 'You can generate up to 2 suggestions',
    }[labelId],
  });

  const hint =
    !hasData && !disabled
      ? intl.formatMessage({
          id: 'components.note-suggest.disabled-hint',
          defaultMessage: 'Add your location and preferences first',
        })
      : null;

  const showPicker = count >= 2;

  return (
    <div className={cx('c-note-suggest', { [className]: !!className })}>
      <button
        type="button"
        className="c-note-suggest__button"
        onClick={handleClick}
        disabled={isDisabled || isLoading}
        aria-busy={isLoading || undefined}
        title={hint || undefined}
      >
        {isLoading ? (
          <Loader className="c-note-suggest__loader" />
        ) : (
          <Icon className="c-note-suggest__icon" aria-hidden />
        )}
        <span>{label}</span>
      </button>
      {showPicker && (
        <div className="c-note-suggest__picker">
          <div className="c-note-suggest__picker-title">
            {intl.formatMessage({
              id: 'components.note-suggest.choose',
              defaultMessage: 'Choose the version you like',
            })}
          </div>
          <Radio.Container
            name="note-suggest-option"
            value={selectedIndex === null ? '' : String(selectedIndex)}
            onChange={handleSelect}
            className="c-radio__container--vertical c-note-suggest__options"
          >
            {suggestions.map((note, index) => (
              <Radio.Option
                key={note}
                value={String(index)}
                className="c-radio--bordered c-radio--with-children c-note-suggest__option"
              >
                <span className="c-radio__children">
                  <span className="c-radio__title">
                    {intl.formatMessage(
                      {
                        id: 'components.note-suggest.option-label',
                        defaultMessage: 'Suggestion {number}',
                      },
                      { number: index + 1 }
                    )}
                  </span>
                  <span className="c-radio__content">{note}</span>
                </span>
              </Radio.Option>
            ))}
          </Radio.Container>
        </div>
      )}
      {errorKey && (
        <div className="c-note-suggest__error" role="alert">
          {intl.formatMessage({
            id: `components.note-suggest.${errorKey}`,
            defaultMessage: {
              error: 'Could not generate a suggestion. Please try again.',
              'error-unavailable': 'This feature is not available yet. Please try later.',
            }[errorKey],
          })}
        </div>
      )}
    </div>
  );
};

export default NoteSuggest;
